// Tinted chips & badges — text color on a faint (12%-alpha) tint of the
// same color, so chips stay legible on glass surfaces in both themes.
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from './IconButton';
import { Button } from './Button';

export interface ChipOption {
  label: string;
  color?: string;
}

// Colored response chip, SC-style: tinted text on faint tinted background.
export function OptionChip({
  option,
  small,
  onRemove,
  disabled,
}: {
  option: ChipOption;
  small?: boolean;
  /** Renders a trailing remove (×) button when provided. */
  onRemove?: () => void;
  disabled?: boolean;
}): JSX.Element {
  const color = option.color ?? '#94a3b8';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md font-semibold ${small ? 'px-1.5 py-0.5 text-caption' : 'px-2 py-0.5 text-label'} ${disabled ? 'opacity-50' : ''}`}
      style={{ color, backgroundColor: `${color}1f` }}
      // Story 2-148 part 7 finding, fixed here: `disabled` only ever drove a
      // visual `opacity-50` — no ARIA signal at all — so the reduced
      // contrast this state deliberately renders at (the same WCAG 1.4.3
      // "inactive component" treatment RadioGroup's `data-disabled` option
      // already carries) had no matching semantic marker for a check (or a
      // screen reader) to recognize this as an intentionally inactive chip
      // rather than a plain low-contrast bug.
      aria-disabled={disabled || undefined}
    >
      {option.label}
      {onRemove && (
        <IconButton
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${option.label}`}
          icon={<XMarkIcon className={small ? 'h-2.5 w-2.5' : 'h-3 w-3'} />}
          size="sm"
          shape="circle"
        />
      )}
    </span>
  );
}

// Clickable sibling of `OptionChip` — story 2-147xd. Extracted from
// `FormPreview.tsx`'s own select/multiselect option pill (identical visual
// contract: neutral outline by default, filled with the option's own
// per-domain colour once selected) rather than a second hand-rolled copy,
// closing that file's design-language/no-inline-style finding by growing
// the vocabulary instead of leaving the inline `style` prop on a composite
// file — the same "OptionChip gets its carve-out only because it lives in
// the exempt primitive layer, the underlying reason is identical either
// way" gap that call site's own comment (story 2-147wg) already named.
// `option.color` is real per-form DATA (set by whoever built the form), not
// a design token — no static Tailwind class can express a runtime-arbitrary
// hex, the same reasoning `OptionChip`'s own `style` already carries.
export function SelectableOptionChip({
  option,
  selected,
  onClick,
}: {
  option: ChipOption;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      variant="ghost"
      size="sm"
      pressed={selected}
      onClick={onClick}
      className={`rounded-full border font-semibold transition ${
        selected ? 'border-transparent text-white' : 'border-border bg-white/50 text-foreground dark:bg-white/10'
      }`}
      style={selected ? { backgroundColor: option.color ?? 'hsl(var(--primary))' } : undefined}
    >
      {option.label}
    </Button>
  );
}

// ColorSwatch / TintedText — story 2-147xd. Two more members of the SAME
// "per-domain arbitrary colour is real DATA, not styling" family `OptionChip`
// above already establishes, extracted to close two more composite/
// product-assembly design-language/no-inline-style findings by growing this
// primitive's own vocabulary instead of leaving `style` on the caller layer:
//   - `ColorSwatch` (was `OrgBrandingPage.tsx`'s inline hex-preview dot next
//     to its color-picker field) — the swatch's OWN visibility toggling
//     (transparent until a full valid hex is typed) stays the caller's own
//     concern, only the "paint a caller-supplied colour" mechanism moves here.
//   - `TintedText` (was `SharedReportPage.tsx`'s org-name heading, server-
//     validated for WCAG AA against its own background at save time —
//     runtime TENANT data, never an app-chrome literal, P10 §4.1's raw-hex
//     rule only forbids the latter) — `as` picks the real semantic element
//     (that call site needs a `<p>`, not a `<span>`).
export function ColorSwatch({ color, className = '' }: { color?: string | undefined; className?: string }): JSX.Element {
  return <span className={`rounded-full border border-border ${className}`} style={{ backgroundColor: color ?? 'transparent' }} aria-hidden="true" />;
}

export function TintedText({
  color,
  fontFamily,
  as = 'span',
  className = '',
  children,
}: {
  color?: string | undefined;
  /**
   * Story 6-9 — a report/portal's org-chosen brand font (a curated CSS
   * font-family STACK string, e.g. `BRAND_FONT_STACKS.outfit` from
   * `lib/branding.ts`), not a raw user string. Same "runtime tenant DATA,
   * not an app-chrome literal" carve-out `color` above already established
   * for this primitive (P10 §4.1's raw-hex rule only forbids the app-chrome
   * case) — typography is the same class of tenant-controlled value color
   * already is here.
   */
  fontFamily?: string | undefined;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const Component = as;
  const style = color || fontFamily ? { ...(color ? { color } : {}), ...(fontFamily ? { fontFamily } : {}) } : undefined;
  return (
    <Component className={className} style={style}>
      {children}
    </Component>
  );
}

type BadgeTone = 'primary' | 'success' | 'warning' | 'destructive' | 'neutral';
type BadgeWeight = 'subtle' | 'bold';
// Story 4-85 — corner treatment, threaded from `ReportStyle.surfaceRadius`
// via `ReportFindingsRollup`. Every OTHER Badge caller app-wide omits this
// prop and keeps the original `rounded-md` look, unchanged.
// Story 2-147qb — added `pill` (`rounded-full`): measured five real,
// independently-evolved call sites (`AgentPanel.tsx`, `TemplateCard.tsx`,
// `TemplateEditorPage.tsx`, plus two inside specialist-renderer formBuilder
// files left as-is, see that story's session record) hand-rolling the exact
// `Badge` visual signature — border+tint+uppercase+tracking-wide — with
// `rounded-full` instead of either existing radius, because a pill shape had
// no home on this component. Not a new component: same tone/weight axes,
// one more shape option.
type BadgeRadius = 'rounded' | 'sharp' | 'pill';

const TONE_VAR: Record<BadgeTone, string> = {
  primary: '--primary',
  success: '--success',
  warning: '--warning',
  destructive: '--destructive',
  neutral: '--muted-foreground',
};

// Story 4-97 — `weight="subtle"` TEXT color only. `hsl(var(--tone))` text on
// `hsl(var(--tone) / 0.1)` background failed WCAG AA 4.5:1 at `text-label`
// size for four of five tones (axe-core found `destructive` first, at
// 4.42:1; a full re-measure found primary/success/neutral also under the
// floor — see tokens.css's own `--{tone}-subtle-text` comment and
// docs/prd/audits/4-97.audit.json for the ratio table). Fixed by darkening
// TEXT only, via a dedicated token per tone — the tint background stays on
// the original `--{tone}` unchanged, so the "faint tinted badge" visual
// language is untouched; only the label reads a shade darker.
const SUBTLE_TEXT_VAR: Record<BadgeTone, string> = {
  primary: '--primary-subtle-text',
  success: '--success-subtle-text',
  warning: '--warning-subtle-text',
  destructive: '--destructive-subtle-text',
  neutral: '--neutral-subtle-text',
};

const RADIUS_CLASS: Record<BadgeRadius, string> = {
  rounded: 'rounded-md',
  sharp: 'rounded-none',
  pill: 'rounded-full',
};

// Semantic-token badge (the badge-soft vocabulary with a tone axis).
export function Badge({ tone = 'primary', weight = 'subtle', radius = 'rounded', color, className = '', children }: {
  tone?: BadgeTone;
  /** `subtle` (default): tinted text on a faint tint. `bold`: solid fill, white text. */
  weight?: BadgeWeight;
  /** `rounded` (default, pre-4-85 unchanged), `sharp` (zero radius) or `pill` (`rounded-full`, story 2-147qb). */
  radius?: BadgeRadius;
  /**
   * Story 4-120 — `ReportFindingsRollup`'s `colorRoles.findingSeverity:
   * 'brand'` override: a runtime hex derived from the report's OWN accent
   * color (never a static app-chrome literal), the SAME tenant-data
   * carve-out `OptionChip`'s own `color` prop already documents (this
   * file's header comment) — overrides `tone`'s token-derived color
   * entirely when set. Every other caller app-wide omits this and keeps
   * the unchanged token-driven look.
   */
  color?: string;
  /** Migration/positioning escape hatch (e.g. `ml-auto`) — merged onto the root, same pattern every other primitive's `className` passthrough already uses. */
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const v = TONE_VAR[tone];
  const subtleTextV = SUBTLE_TEXT_VAR[tone];
  const style = color
    ? weight === 'bold'
      ? { color: '#fff', backgroundColor: color, borderColor: color }
      : { color, backgroundColor: `${color}1a`, borderColor: `${color}40` }
    : weight === 'bold'
      ? { color: '#fff', backgroundColor: `hsl(var(${v}))`, borderColor: `hsl(var(${v}))` }
      : {
          // Story 4-97: text reads the darkened `-subtle-text` token, NOT
          // `v` — background/border stay on the original `--{tone}` so the
          // tint itself is unchanged.
          color: `hsl(var(${subtleTextV}))`,
          backgroundColor: `hsl(var(${v}) / 0.1)`,
          borderColor: `hsl(var(${v}) / 0.25)`,
        };
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-label font-semibold uppercase tracking-wide ${RADIUS_CLASS[radius]} ${className}`}
      style={style}
    >
      {children}
    </span>
  );
}
